import { useEffect, useState } from 'react';
import { Navigate, Outlet, useParams, useSearchParams } from 'react-router-dom';
import { useProjetos } from '../api/consultas';
import { useSessao } from '../auth/sessao';
import { Carregando, Falha, Vazio } from '../componentes/Estado';
import { Paleta } from '../componentes/Paleta';
import { Tour } from '../tour/Tour';
import { ABA_PADRAO, abaExiste } from './catalogo';
import { Abas } from './Abas';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';

// A casca é o que sobrevive à troca de aba: topbar, lista de abas e a moldura de
// duas colunas. O conteúdo entra pelo Outlet, e cada tela é responsável pelos
// seus três estados.
//
// O slug e a aba vêm da URL, não de variável de módulo como o `SLUG` global do
// painel atual. É o que faz F5 cair na mesma tela e o link colado no chat abrir
// onde o outro parou.
export function Casca() {
  const { slug = '', aba } = useParams();
  const { admin, eu } = useSessao();
  const projetos = useProjetos();
  const [busca, setBusca] = useState('');
  const [paleta, setPaleta] = useState(false);
  const [tour, setTour] = useState(false);
  const [parametros] = useSearchParams();

  // link de busca colado no chat tem de encher o campo do topo, senão a tela
  // mostra o resultado de um termo que o campo não tem — e o próximo Enter
  // busca vazio. No painel atual isso era o `$('#q').value=setQ` de app.js:608.
  const termo = parametros.get('q');
  useEffect(() => {
    if (aba === 'search' && termo !== null) setBusca(termo);
  }, [aba, termo]);

  // Ctrl+K vive na casca porque a paleta atravessa as abas: ela indexa o que
  // qualquer tela já carregou e leva para qualquer outra (knowledge.js:756).
  useEffect(() => {
    function noTeclado(e: KeyboardEvent) {
      if (!(e.ctrlKey || e.metaKey) || (e.key !== 'k' && e.key !== 'K')) return;
      e.preventDefault();
      setPaleta((aberta) => !aberta);
    }
    document.addEventListener('keydown', noTeclado);
    return () => document.removeEventListener('keydown', noTeclado);
  }, []);

  if (projetos.isPending) {
    return <div style={{ padding: 30 }}><Carregando rotulo="seus projetos" /></div>;
  }
  if (projetos.error) {
    return (
      <div style={{ padding: 30 }}>
        <Falha erro={projetos.error} rotulo="a lista de projetos" aoRepetir={projetos.refetch} />
      </div>
    );
  }

  const lista = projetos.data ?? [];

  if (!lista.length) {
    return (
      <>
        <Topbar
          slug={null}
          projetos={lista}
          busca={busca}
          aoBuscar={setBusca}
          aoAbrirPaleta={() => setPaleta(true)}
          aoAbrirTour={() => setTour(true)}
        />
        <div className="main">
          <div className="content-card">
            <div className="pane active">
              <Vazio titulo="Nenhum projeto liberado para você">
                Sua conta (<code>{eu?.email}</code>) está ativa, mas ainda não tem acesso a nenhum
                acervo. Um admin libera na aba <b>Usuários</b>, marcando os projetos e salvando o acesso.
              </Vazio>
            </div>
          </div>
        </div>
      </>
    );
  }

  // slug fora da lista é link antigo ou acesso revogado; o primeiro projeto é a
  // resposta honesta, e trocar a URL evita o F5 cair no mesmo 404
  if (!lista.some((p) => p.slug === slug)) return <Navigate to={`/p/${lista[0].slug}`} replace />;
  if (!aba || !abaExiste(aba, admin)) return <Navigate to={`/p/${slug}/${ABA_PADRAO}`} replace />;

  return (
    <>
      <a className="skip" href="#pane-overview">pular para o conteúdo</a>
      <Topbar
        slug={slug}
        projetos={lista}
        busca={busca}
        aoBuscar={setBusca}
        aoAbrirPaleta={() => setPaleta(true)}
        aoAbrirTour={() => setTour(true)}
      />
      <Abas slug={slug} ativa={aba} />
      <div className="main">
        <Sidebar slug={slug} />
        <div className="content-card">
          <div className="pane active" id={`pane-${aba}`} role="tabpanel" tabIndex={0}>
            <Outlet />
          </div>
        </div>
      </div>
      <Paleta
        slug={slug}
        aberta={paleta}
        aoFechar={() => setPaleta(false)}
        aoAbrirTour={() => setTour(true)}
      />
      <Tour slug={slug} aberto={tour} aoFechar={() => setTour(false)} />
    </>
  );
}
